import "./styles.scss";

import { forwardRef, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import classnames from "classnames";

import MobileBar from "components/MobileBar";
import BackMobile from "components/BackMobile";
import Button from "components/Button";
import Loader from "components/Loader";
import IconButton from "components/IconButton";
import ScrollTopMobile from "components/ScrollTopMobile";

import useResponsive from "libs/helpers/useResponsive";
import useMergeRefs from "hooks/useMergeRefs";

import { ReactComponent as CrossIcon } from "assets/icons/modal-cross.svg";

const mainClass = "modal-new";

const ModalNew = forwardRef(
  (
    {
      children,
      className,
      title,
      description,
      variant,
      status,
      loading,
      dataLoading,
      labelSubmit = "Update data",
      hasMobileScrollTop = true,
      isOpen,
      isSubmitDisabled,
      isSubmitHidden,
      isBackdropDisabled,
      onSubmit,
      onClose,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { max } = useResponsive();

    const innerRef = useRef(null);
    const mergedRef = useMergeRefs(ref, innerRef);

    const isDelete = variant === "delete";

    useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }

      return () => (document.body.style.overflow = "");
    }, [isOpen]);

    const handleClose = () => {
      if (typeof onClose === "function") onClose();
    };

    return createPortal(
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={mergedRef}
            className={classnames(mainClass, className, {
              [`${mainClass}--${variant}`]: !!variant,
              [`${mainClass}--${status}`]: !!status,
            })}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: "tween", duration: max.sm ? 0.2 : 0 }}
          >
            <div className={`${mainClass}__container`}>
              <div className={`${mainClass}__wrapper`}>
                {max.sm && !isDelete && (
                  <>
                    <MobileBar isStatic onWebsiteClick={handleClose} />
                    {onClose && (
                      <div className={`${mainClass}__back`}>
                        <BackMobile onClick={handleClose} />
                      </div>
                    )}
                  </>
                )}
                {!!title && (
                  <div className={`${mainClass}__header`}>
                    <h3>{title}</h3>
                    {!!description && <p>{description}</p>}
                    {!loading && (
                      <IconButton
                        variant="medium"
                        onClick={handleClose}
                        icon={<CrossIcon />}
                      />
                    )}
                  </div>
                )}
                <div className={`${mainClass}__content`}>
                  {!!!dataLoading ? (
                    children
                  ) : (
                    <Loader color="blue" size="large" />
                  )}
                </div>
                {!isSubmitHidden && (
                  <div className={`${mainClass}__action`}>
                    <Button
                      loading={loading}
                      disabled={isSubmitDisabled}
                      onClick={onSubmit}
                    >
                      {t(labelSubmit)}
                    </Button>
                  </div>
                )}
                {hasMobileScrollTop && max.sm && (
                  <div className={`${mainClass}__scroll`}>
                    <ScrollTopMobile el={innerRef} />
                  </div>
                )}
              </div>
            </div>
            <div
              className={`${mainClass}__backdrop`}
              onClick={!isBackdropDisabled ? () => handleClose() : undefined}
            />
          </motion.div>
        )}
      </AnimatePresence>,
      document.body,
    );
  },
);

export default ModalNew;
